import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, Mail, Instagram, Facebook } from "lucide-react";
import ReturnForm from "@/components/site/ReturnForm";

const LOGO_ICON = "/logo-emblem.png";

const POLICIES = [
  {
    id: "terms",
    title: "Terms & Conditions",
    body: [
      "Stud Exchange (\"we\", \"us\", \"our\") operates this website for the buying, selling and trading of LEGO sets, minifigures and collectibles. By browsing or purchasing from this site, you agree to these terms.",
      "We describe and photograph every item in good faith and to the best of our expertise. Product images show the actual item unless stated otherwise, though colours may vary slightly between screens.",
      "All prices are listed in pounds sterling (GBP) and may change without notice. In the rare event of a pricing or listing error, we reserve the right to cancel the affected order and issue a full refund.",
      "Store credit codes issued by us hold a running balance, never expire, and can be redeemed only on this website against in-stock items. They are not exchangeable for cash, and no change is given other than as remaining credit on the same code. Treat codes like cash — anyone holding a code can spend it, so keep yours private.",
      "All content on this site — including our logo, branding, photography and text — belongs to Stud Exchange and may not be reused without permission. LEGO® is a trademark of the LEGO Group, which does not sponsor, authorise or endorse this site.",
      "Our liability for any order is limited to the total value of that order. These terms are governed by the laws of Scotland.",
    ],
  },
  {
    id: "returns",
    title: "Returns & Refunds",
    body: [
      "Changed your mind? You have 14 days from the day your order is delivered to let us know you'd like to return it, in line with UK consumer regulations. The quickest way is the form below — or email us at Officialstudexchange@hotmail.com.",
      "Items must be returned in exactly the condition they arrived in — factory-sealed sets must remain sealed, and minifigures must include all original accessories and packaging where applicable.",
      "Once we receive and inspect the return, your refund is issued to the original payment method within 14 days.",
      "Return postage is covered by the buyer unless the item arrived faulty, damaged in transit, or not as described — in those cases we cover return costs and put things right as quickly as we can.",
    ],
  },
  {
    id: "shipping",
    title: "Shipping & Delivery",
    body: [
      "Every order is packed with collector-grade care — boxed, protected and sealed — and dispatched within 2 working days of cleared payment.",
      "All parcels are sent via tracked and insured services, so your grails are covered door to door. You'll receive tracking as soon as your order ships.",
      "Shipping costs are confirmed with you by email at the time of your order — you'll always know the full cost before anything is dispatched. UK delivery typically takes 2–4 working days after dispatch.",
      "Collecting from outside the UK? International shipping is available on request — contact us before or after ordering and we'll quote you a tracked, insured rate.",
      "High-value orders may require a signature on delivery. If a parcel arrives damaged, photograph the packaging before opening and contact us immediately.",
    ],
  },
  {
    id: "privacy",
    title: "Privacy Policy",
    body: [
      "We collect the details you give us — your name, email address, and any photos, videos or descriptions you send with valuation, selling or return requests — plus the order details needed to fulfil purchases.",
      "If you sell to us, we also collect what the deal needs to complete: bank transfer details for cash payouts (account holder name, sort code and account number — never card details), any postage tracking numbers you share, and the store credit codes and balances issued to you. These are stored securely and are visible only to us for the purpose of completing your deal.",
      "We use this information only to respond to your requests, value your items, pay you, fulfil orders, and run the website. We never sell your data to anyone.",
      "Payments from buyers are processed securely by Stripe — we never see or store your full card details. Operational data is handled only by the service providers needed to run the site (hosting, email, file storage and payment processing).",
      "Uploaded photos, bank details and submission details are kept only as long as needed to handle your request and any resulting sale, or as required by law (for example, accounting records).",
      "Under UK GDPR you can ask to see, correct or delete your personal data at any time — email Officialstudexchange@hotmail.com and we'll action it promptly.",
    ],
  },
  {
    id: "cookies",
    title: "Cookie Policy",
    body: [
      "This site keeps things minimal. We use only essential storage: a secure sign-in cookie for the admin area, and device-side storage that remembers your basket, wishlist and browsing preferences (like how many items you view per page).",
      "Your basket, wishlist and preferences are stored on your own device and are never sent to us.",
      "We use no advertising, marketing or third-party tracking cookies.",
      "You can clear this data anytime through your browser's settings — doing so simply resets your saved preferences and wishlist.",
    ],
  },
  {
    id: "authenticity",
    title: "Authenticity & Condition",
    body: [
      "Every piece that enters the Stud Exchange vault goes through a multi-point inspection: part authenticity, print quality, colour consistency, completeness and condition of packaging where applicable.",
      "Condition grades — such as Factory Sealed, Mint, Like New or Vault Grade — are our honest expert assessment, given in good faith to help you buy with confidence.",
      "Unless stated otherwise, photographs show the actual item you will receive, including any flaws we consider worth noting.",
      "If you ever believe an item isn't as described, contact us within 14 days of delivery with photos — we'll always make it right, whether that's a return, a refund or a replacement.",
    ],
  },
  {
    id: "selling",
    title: "Selling & Valuation Terms",
    body: [
      "Our valuations are free, honest market estimates based on recent sales and collector demand. A valuation is an appraisal, not a binding offer to purchase, and is valid for 7 days.",
      "When you send us an offer to sell or trade, we may accept, decline, counter, or offer store credit with a trade bonus. Every request gets a reference number (like SX-0001) — you can check its status and respond to any offer, at any time, on our Offer Tracker page. Nothing is agreed until you accept on the tracker or we confirm in writing by email, and you're free to decline at any point.",
      "Once a deal is agreed, we'll email you full postage and packing instructions — including our postal address — so there's no back-and-forth. Please write your reference number on a slip of paper inside the parcel, use a tracked service, and let us know it's on its way via the \"I've Posted It\" button on your tracker page (you can add your tracking number there too).",
      "Payment is made after we receive and verify the items match their description. Cash deals are paid by UK bank transfer (Faster Payments) to the account details you give us on your tracker page — we will never ask for card details. If items differ materially from their description, we'll discuss a revised offer or return them — return arrangements will always be agreed with you first.",
      "Store credit is issued as a unique code with a running balance — spend it all at once or a little at a time. It never expires and can only be spent on this website. Treat your code like cash: anyone holding it can use it, so keep it private. If you lose it, your code and balance are always recoverable from your tracker page using your reference and email. Store credit has no cash alternative.",
      "You confirm that any items you offer to us are yours, that you have the right to sell them, and that your description and photos are accurate.",
      "We may decline any submission at our discretion. Sending items to us without an agreed sale in place is at your own risk, so always wait for your postage instructions before sending anything.",
    ],
  },
  {
    id: "contact",
    title: "Contact / Business Details",
    body: [
      "Stud Exchange — Buy • Sell • Trade • Collect.",
      "Email: Officialstudexchange@hotmail.com — we aim to reply within 24 hours on business days.",
      "Socials: Instagram @StudExchangeOfficial and Facebook at Stud Exchange Official.",
      "For order queries, please include your order or checkout reference so we can help faster. For valuations and selling enquiries, the forms on our homepage are the quickest route.",
      "Already dealing with us? Track any valuation, offer or return — and respond to offers, tell us your parcel is posted, or save your payout details — on the Offer Tracker page with your reference number and email.",
    ],
  },
];

export default function Policies() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.hash]);

  return (
    <main className="min-h-screen bg-[#F3EDE3] text-[#0A0A0A] font-sans antialiased" data-testid="policies-page">
      <header className="border-b border-[#DCD3C3] bg-[#F3EDE3]/90 backdrop-blur sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="policies-logo">
            <img src={LOGO_ICON} alt="Stud Exchange" className="w-10 h-10 rounded-full object-cover" />
            <div className="leading-none">
              <p className="font-serif font-semibold tracking-wide text-sm">STUD EXCHANGE</p>
              <p className="font-mono text-[9px] tracking-[0.3em] text-[#B38728] mt-1">BUY • SELL • TRADE • COLLECT</p>
            </div>
          </Link>
          <Link to="/" data-testid="policies-back-link" className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-[#5C564C] hover:text-[#B38728] transition-colors">
            <ArrowLeft className="w-4 h-4" /> Storefront
          </Link>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-14 md:py-20">
        <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#B38728] flex items-center gap-3">
          <span className="inline-block w-10 h-px bg-[#B38728]" /> The Fine Print
        </p>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-5">Policies & Terms</h1>
        <p className="text-[#5C564C] text-sm mt-4 max-w-xl">
          Everything in plain English — how we buy, sell, ship, value and look after your data. Last updated September 2026.
        </p>

        <nav className="mt-8 flex flex-wrap gap-2" data-testid="policies-nav">
          {POLICIES.map((p) => (
            <a
              key={p.id}
              href={`#${p.id}`}
              data-testid={`policy-nav-${p.id}`}
              className="px-4 py-2 border border-[#DCD3C3] font-mono text-[10px] uppercase tracking-[0.2em] text-[#5C564C] hover:border-[#B38728] hover:text-[#B38728] transition-colors"
            >
              {p.title}
            </a>
          ))}
        </nav>

        <div className="mt-12 space-y-6">
          {POLICIES.map((p, i) => (
            <section key={p.id} id={p.id} className="scroll-mt-28 bg-white border border-[#DCD3C3] p-8 md:p-10" data-testid={`policy-section-${p.id}`}>
              <div className="flex items-baseline gap-4">
                <span className="font-mono text-xs tracking-[0.25em] gold-text">{String(i + 1).padStart(2, "0")}</span>
                <h2 className="font-serif text-2xl sm:text-3xl">{p.title}</h2>
              </div>
              <div className="mt-5 space-y-4">
                {p.body.map((para, j) => (
                  <p key={j} className="text-sm text-[#5C564C] leading-relaxed">{para}</p>
                ))}
                {p.id === "returns" && <ReturnForm />}
                {p.id === "contact" && (
                  <div className="flex flex-wrap gap-3 pt-2">
                    <a href="mailto:Officialstudexchange@hotmail.com" data-testid="policy-contact-email" className="flex items-center gap-2 bg-[#0A0A0A] text-[#F3EDE3] px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] hover:bg-[#B38728] hover:text-[#0A0A0A] transition-colors">
                      <Mail className="w-3.5 h-3.5" /> Email Us
                    </a>
                    <a href="https://instagram.com/StudExchangeOfficial" target="_blank" rel="noopener noreferrer" data-testid="policy-contact-instagram" className="flex items-center gap-2 border border-[#DCD3C3] px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C564C] hover:border-[#B38728] hover:text-[#B38728] transition-colors">
                      <Instagram className="w-3.5 h-3.5" /> Instagram
                    </a>
                    <a href="https://facebook.com/StudExchangeOfficial" target="_blank" rel="noopener noreferrer" data-testid="policy-contact-facebook" className="flex items-center gap-2 border border-[#DCD3C3] px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C564C] hover:border-[#B38728] hover:text-[#B38728] transition-colors">
                      <Facebook className="w-3.5 h-3.5" /> Facebook
                    </a>
                  </div>
                )}
              </div>
            </section>
          ))}
        </div>

        <p className="mt-10 text-xs text-[#5C564C] text-center">
          Questions about any of the above? <a href="mailto:Officialstudexchange@hotmail.com" className="text-[#B38728] hover:underline">Officialstudexchange@hotmail.com</a>
        </p>
      </div>
    </main>
  );
}
